export interface HubCategory {
  id: string;
  name: string;
  icon: string;
  subcategories?: string[];
  itemTypes?: Record<string, string[]>; // Mapping subcategory name to a list of specific item types
}

export interface HubConfig {
  title: string;
  description: string;
  color: string;
  image: string;
  accentColor: string;
  locationType: 'neighborhood' | 'county' | 'global';
}

export const HUB_CONFIG: Record<string, HubConfig> = {
  hotels: {
    title: '🏨 Pambo Hotel Plus',
    description: 'List and find hotels, guest houses, Airbnbs & resorts across Kenya.',
    color: 'from-amber-500 to-orange-600',
    accentColor: '#F59E0B',
    locationType: 'neighborhood',
    image:
      'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=80',
  },
  logistics: {
    title: '🚚 Logistics & Delivery',
    description: 'Moving services, courier booking & freight transport across Kenya.',
    color: 'from-yellow-600 to-orange-700',
    accentColor: '#D97706',
    locationType: 'neighborhood',
    image:
      'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1200&q=80',
  },
  retailers: {
    title: '🏪 Retailers Marketplace',
    description: 'General marketplace for everyday items',
    color: 'from-blue-600 to-indigo-700',
    accentColor: '#2563eb',
    locationType: 'neighborhood',
    image:
      'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&w=1200&q=80',
  },
  wholesalers: {
    title: '📦 Wholesalers Market',
    description: 'Bulk items and large quantities',
    color: 'from-blue-800 to-indigo-900',
    accentColor: '#1e40af',
    locationType: 'neighborhood',
    image:
      'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80',
  },
  secondhand: {
    title: '♻️ Secondhand Soko',
    description: 'Pre-owned deals and recycled finds',
    color: 'from-emerald-600 to-teal-700',
    accentColor: '#059669',
    locationType: 'neighborhood',
    image:
      'https://images.unsplash.com/photo-1533900298318-6b8da08a523e?auto=format&fit=crop&w=1200&q=80',
  },
  farmer: {
    title: '🌾 Farmer Produce',
    description: 'Direct farm produce and livestock',
    color: 'from-green-600 to-emerald-800',
    accentColor: '#166534',
    locationType: 'neighborhood',
    image:
      'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?auto=format&fit=crop&w=1200&q=80',
  },
  digital: {
    title: '💻 Digital Products',
    description: 'Software, tools & digital services',
    color: 'from-purple-600 to-pink-700',
    accentColor: '#9333ea',
    locationType: 'global',
    image:
      'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=1200&q=80',
  },
  services: {
    title: '🔧 Services',
    description: 'Local professionals and artisans',
    color: 'from-indigo-600 to-blue-800',
    accentColor: '#4f46e5',
    locationType: 'neighborhood',
    image:
      'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=80',
  },
  tech: {
    title: '⚡ Tech & Electronics',
    description: 'Phones, laptops, accessories & smart gadgets',
    color: 'from-cyan-600 to-blue-700',
    accentColor: '#0891b2',
    locationType: 'neighborhood',
    image:
      'https://images.unsplash.com/photo-1468495244123-6c6c332eeece?auto=format&fit=crop&w=1200&q=80',
  },
  cars: {
    title: '🚗 Selling Cars',
    description: 'New & used cars, commercial vehicles, and auto parts.',
    color: 'from-red-600 to-rose-700',
    accentColor: '#e11d48',
    locationType: 'neighborhood',
    image:
      'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=1200&q=80',
  },
  'real-estate': {
    title: '🏠 Real Estate',
    description: 'Buy, sell, and rent properties, land, and commercial spaces.',
    color: 'from-blue-700 to-sky-600',
    accentColor: '#0369a1',
    locationType: 'neighborhood',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80',
  },
  fashion: {
    title: '👕 Fashion Hub',
    description: 'Clothing, shoes, accessories, and trendy apparel.',
    color: 'from-pink-600 to-rose-700',
    accentColor: '#db2777',
    locationType: 'neighborhood',
    image: 'https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=1200&q=80',
  },
  'kitchen-equipment': {
    title: '🍳 Kitchen Equipment',
    description: 'Commercial & domestic kitchen equipment, utensils, and appliances.',
    color: 'from-orange-600 to-amber-700',
    accentColor: '#d97706',
    locationType: 'neighborhood',
    image: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=1200&q=80',
  },
};

export const HUB_CATEGORIES: Record<string, HubCategory[]> = {
  'real-estate': [
    {
      id: 'property',
      name: 'Property',
      icon: '🏠',
      subcategories: ['Apartments', 'Houses', 'Land', 'Commercial Property'],
      itemTypes: {
        'Apartments': ['Bedsitter', '1 Bedroom', '2 Bedroom', '3 Bedroom', '4+ Bedroom', 'Penthouse'],
        'Houses': ['Townhouse', 'Mansionatte', 'Bungalow', 'Villa', 'Cottage'],
        'Land': ['Agricultural Land', 'Commercial Land', 'Residential Land', 'Plots'],
        'Commercial Property': ['Office Space', 'Shop / Retail', 'Warehouse', 'Industrial'],
      }
    }
  ],
  fashion: [
    {
      id: 'mens-clothing',
      name: 'Men’s Clothing',
      icon: '👔',
      subcategories: [
        'T-Shirts', 'Shirts', 'Polo Shirts', 'Tank Tops', 'Hoodies', 'Sweatshirts', 'Jackets', 'Coats', 'Blazers', 'Suits', 'Jeans', 'Trousers', 'Chinos', 'Shorts', 'Cargo Pants', 'Tracksuits', 'Joggers', 'Underwear', 'Sleepwear', 'Sportswear', 'Traditional Wear', 'Sweaters', 'Workwear', 'Plus Size Clothing', 'Designer Clothing'
      ]
    },
    {
      id: 'womens-clothing',
      name: 'Women’s Clothing',
      icon: '👗',
      subcategories: [
        'Dresses', 'Evening Gowns', 'Skirts', 'Tops', 'Blouses', 'T-Shirts', 'Shirts', 'Hoodies', 'Sweatshirts', 'Jackets', 'Coats', 'Blazers', 'Suits', 'Jeans', 'Trousers', 'Leggings', 'Shorts', 'Jumpsuits & Rompers', 'Lingerie', 'Underwear', 'Shapewear', 'Sleepwear', 'Traditional Wear', 'Sportswear', 'Swimwear', 'Maternity Wear', 'Plus Size Clothing', 'Designer Clothing'
      ]
    },
    {
      id: 'shoes',
      name: 'Shoes & Footwear',
      icon: '👟',
      subcategories: [
        'Sneakers', 'Formal Shoes', 'Boots', 'Sandals', 'Slippers', 'Heels', 'Flats', 'Loafers', 'Oxfords', 'Sports Shoes', 'Running Shoes', 'Football Boots', 'School Shoes', 'Canvas Shoes', 'Designer Shoes'
      ]
    },
    {
      id: 'accessories',
      name: 'Accessories',
      icon: '👜',
      subcategories: [
        'Bags & Purses', 'Backpacks', 'Wallets', 'Watches', 'Jewelry', 'Sunglasses', 'Hats & Caps', 'Belts', 'Scarves & Gloves'
      ]
    },
    {
      id: 'kids-clothing',
      name: 'Kids Clothing',
      icon: '👶',
      subcategories: [
        'Boys Clothing', 'Girls Clothing', 'Baby Clothing', 'School Uniforms'
      ]
    }
  ],
  'kitchen-equipment': [
    {
      id: 'commercial-kitchen',
      name: 'Commercial Equipment',
      icon: '🏭',
      subcategories: [
        'Commercial Ovens', 'Deep Fryers', 'Grills & Griddles', 'Commercial Refrigeration', 'Dough Mixers', 'Bakery Equipment', 'Display Chillers'
      ]
    },
    {
      id: 'domestic-appliances',
      name: 'Domestic Appliances',
      icon: '🔌',
      subcategories: [
        'Cookers & Ovens', 'Microwaves', 'Refrigerators & Freezers', 'Blenders & Mixers', 'Air Fryers', 'Coffee Makers', 'Water Dispensers'
      ]
    },
    {
      id: 'kitchen-utensils',
      name: 'Utensils & Cookware',
      icon: '🔪',
      subcategories: [
        'Pots & Pans', 'Cutlery', 'Plates & Bowls', 'Cooking Spoons & Spatulas', 'Baking Trays', 'Knives & Boards'
      ]
    },
    {
      id: 'kitchen-furniture',
      name: 'Kitchen Furniture & Sinks',
      icon: '🚰',
      subcategories: [
        'Kitchen Cabinets', 'Kitchen Sinks', 'Kitchen Islands', 'Dining Tables', 'Shelves & Racks'
      ]
    }
  ],
  cars: [
    {
      id: 'passenger-cars',
      name: 'Passenger Cars',
      icon: '🚗',
      subcategories: [
        'Sedans & Saloons',
        'SUVs & 4x4s',
        'Hatchbacks',
        'Station Wagons',
        'Coupes & Sports Cars',
      ],
      itemTypes: {
        'Sedans & Saloons': [
          'Toyota Corolla',
          'Mercedes Benz C-Class',
          'BMW 3 Series',
          'Nissan Sylphy',
          'Mazda Axela',
          'Other Sedans',
        ],
        'SUVs & 4x4s': [
          'Toyota Land Cruiser',
          'Toyota Prado',
          'Range Rover',
          'Nissan X-Trail',
          'Subaru Forester',
          'Other SUVs',
        ],
        Hatchbacks: [
          'Toyota Vitz',
          'Honda Fit',
          'Mazda Demio',
          'VW Golf',
          'Suzuki Swift',
          'Other Hatchbacks',
        ],
        'Station Wagons': [
          'Toyota Fielder',
          'Subaru Outback',
          'Honda Stream',
          'Nissan Wingroad',
          'Other Wagons',
        ],
      },
    },
    {
      id: 'commercial',
      name: 'Commercial Vehicles',
      icon: '🚚',
      subcategories: [
        'Pickups & Double Cabs',
        'Trucks & Lorries',
        'Vans & Minibuses',
        'Buses',
        'Tractors & Machinery',
      ],
      itemTypes: {
        'Pickups & Double Cabs': [
          'Toyota Hilux',
          'Isuzu D-Max',
          'Ford Ranger',
          'Mitsubishi L200',
          'Other Pickups',
        ],
        'Trucks & Lorries': ['Isuzu NQR', 'Mitsubishi Canter', 'FH', 'Heavy Duty Trucks'],
        'Vans & Minibuses': ['Toyota Hiace', 'Nissan Caravan', 'Nissan NV200', 'Matatus'],
      },
    },
    {
      id: 'bikes',
      name: 'Bikes & Scooters',
      icon: '🏍️',
      subcategories: ['Motorcycles', 'Scooters', 'Electric Bikes', 'Bicycles'],
      itemTypes: {
        Motorcycles: ['Bodabodas', 'TVS', 'Boxer', 'Sports Bikes', 'Dirt Bikes'],
      },
    },
    {
      id: 'parts',
      name: 'Auto Parts & Accessories',
      icon: '⚙️',
      subcategories: ['Engine Parts', 'Body Parts', 'Tyres & Rims', 'Car Audio', 'Accessories'],
      itemTypes: {
        'Engine Parts': ['Engines', 'Gearboxes', 'Radiators', 'Batteries'],
        'Body Parts': ['Bumpers', 'Doors', 'Mirrors', 'Lights'],
        'Tyres & Rims': ['Tyres', 'Alloy Rims', 'Steel Rims'],
        Accessories: ['Car Mats', 'Seat Covers', 'Dash Cams', 'GPS Trackers'],
      },
    },
  ],
  retailers: [
    {
      id: 'electronics',
      name: 'Electronics',
      icon: '🧱',
      subcategories: [
        'Mobile Phones & Tablets',
        'Computers & IT',
        'TV & Home Audio',
        'Photography & Drones',
        'Gaming',
        'Smart Home',
        'Power & Accessories',
      ],
      itemTypes: {
        'Mobile Phones & Tablets': [
          'Smartphones',
          'Feature Phones',
          'Tablets (Android)',
          'Tablets (iPad)',
          'Smartwatches',
          'Kindles & E-readers',
        ],
        'Computers & IT': [
          'Laptops',
          'Desktop Computers',
          'Monitors',
          'Printers & Scanners',
          'Networking Equipment (Routers)',
          'External Storage',
          'Computer Accessories',
        ],
        'TV & Home Audio': [
          'Smart TVs',
          'OLED/QLED TVs',
          'Home Theater Systems',
          'Soundbars',
          'Bluetooth Speakers',
          'Headphones',
          'Earphones & Buds',
        ],
        'Photography & Drones': [
          'Digital Cameras',
          'DSLR Cameras',
          'Action Cameras',
          'Drones',
          'Camera Lenses',
          'Tripods & Stands',
        ],
        Gaming: [
          'PlayStation Consoles',
          'Xbox Consoles',
          'Nintendo Switch',
          'Gaming PCs',
          'Gaming Controllers',
          'Gaming Headsets',
        ],
        'Smart Home': [
          'Smart Security Cameras',
          'Smart Lights',
          'Smart Doorlocks',
          'Smart Speakers',
          'Robot Vacuum Cleaners',
        ],
        'Power & Accessories': [
          'Power Banks',
          'Chargers & Cables',
          'UPS & Inverters',
          'Batteries',
          'Electronic Accessories',
        ],
      },
    },
    {
      id: 'home-furniture',
      name: 'Home & Furniture',
      icon: '🛋️',
      subcategories: [
        'Sofas & Couches',
        'Beds & Mattresses',
        'Wardrobes',
        'Tables',
        'Chairs',
        'Office Furniture',
        'TV Stands',
        'Cabinets',
        'Shelves',
        'Carpets & Rugs',
        'Curtains & Blinds',
        'Lighting',
        'Wall Decor',
        'Kitchen Furniture',
        'Bathroom Furniture',
        'Outdoor Furniture',
        'Storage Units',
        'Dining Sets',
        'Home Accessories',
        'Interior Decor',
      ],
    },
    {
      id: 'home-appliances',
      name: 'Home Appliances',
      icon: '🔌',
      subcategories: [
        'Cooling & Refrigeration',
        'Cooking Appliances',
        'Small Kitchen Appliances',
        'Laundry & Cleaning',
        'Air & Water Climate',
        'Other Home Appliances',
      ],
      itemTypes: {
        'Cooling & Refrigeration': [
          'Refrigerators',
          'Mini Fridges',
          'Deep Freezers',
          'Wine Coolers',
          'Water Dispensers',
          'Ice Makers',
        ],
        'Cooking Appliances': [
          'Cookers',
          'Gas Cookers',
          'Electric Cookers',
          'Ovens',
          'Microwaves',
          'Dishwashers',
          'Hot Plates',
          'Induction Cooktops',
        ],
        'Small Kitchen Appliances': [
          'Air Fryers',
          'Toasters',
          'Electric Kettles',
          'Coffee Makers',
          'Blenders',
          'Food Processors',
          'Juicers',
          'Mixers',
          'Electric Pressure Cookers',
          'Rice Cookers',
          'Slow Cookers',
          'Egg Boilers',
          'Sandwich Makers',
          'Plate Warmers',
        ],
        'Laundry & Cleaning': [
          'Washing Machines',
          'Dryers',
          'Washer-Dryer Combos',
          'Irons',
          'Steam Irons',
          'Garment Steamers',
          'Vacuum Cleaners',
          'Robot Vacuum Cleaners',
          'Carpet Cleaners',
        ],
        'Air & Water Climate': [
          'Air Conditioners',
          'Fans',
          'Air Coolers',
          'Air Purifiers',
          'Humidifiers',
          'Dehumidifiers',
          'Water Heaters',
          'Instant Water Heaters',
          'Solar Water Heaters',
          'Shower Heads (Electric)',
        ],
      },
    },
    {
      id: 'vehicles',
      name: 'Vehicles & Transport',
      icon: '🚗',
      subcategories: [
        'Passenger Vehicles',
        'Bikes & Scooters',
        'Commercial & Heavy Duty',
        'Parts & Accessories',
        'Auto Services & Care',
        'Other Transport',
      ],
      itemTypes: {
        'Passenger Vehicles': [
          'Saloon / Sedan',
          'SUV / 4x4',
          'Hatchback',
          'Station Wagon',
          'Coupe / Sports Car',
          'Convertible',
          'Pickup Trucks',
        ],
        'Bikes & Scooters': [
          'Motorcycles / Bodaboda',
          'Sports Bikes',
          'Scooters',
          'Bicycles',
          'Electric Bikes',
        ],
        'Commercial & Heavy Duty': [
          'Trucks / Lorries',
          'Buses & Matatus',
          'Vans',
          'Trailers',
          'Construction Machinery',
          'Tractors',
        ],
        'Parts & Accessories': [
          'Engines & Drivetrain',
          'Tires & Rims',
          'Body Parts',
          'Lights & Indicators',
          'Car Electronics / Audio',
          'Brakes & Suspension',
          'Interior Accessories',
        ],
        'Auto Services & Care': [
          'Car Wash',
          'Mechanical Repair',
          'Body Work / Painting',
          'Wheel Alignment',
          'Car Care Products',
        ],
        'Other Transport': ['Boats & Watercraft', 'Electric Vehicles', 'Security & GPS Systems'],
      },
    },
    {
      id: 'beauty',
      name: 'Beauty & Personal Care',
      icon: '💄',
      subcategories: [
        'Skincare',
        'Makeup',
        'Hair Care',
        'Wigs & Extensions',
        'Perfumes',
        'Body Care',
        'Men Grooming',
        'Beauty Tools',
        'Nail Products',
        'Spa Products',
        'Organic Beauty',
        'Cosmetics Sets',
        'Facial Care',
        'Anti-aging Products',
        'Hair Tools',
        'Barber Supplies',
        'Salon Equipment',
        'Makeup Kits',
        'Beauty Accessories',
        'Personal Hygiene',
      ],
    },
    {
      id: 'kids-baby',
      name: 'Kids & Baby',
      icon: '👶',
      subcategories: [
        'Baby Clothing',
        'Kids Clothing',
        'Toys',
        'Educational Toys',
        'Baby Gear',
        'Strollers',
        'Car Seats',
        'Baby Care',
        'Diapers',
        'Feeding Products',
        'Baby Furniture',
        'Kids Shoes',
        'School Supplies',
        'Books',
        'Outdoor Toys',
        'Playsets',
        'Baby Accessories',
        'Safety Products',
        'Nursery Items',
        'Kids Electronics',
      ],
    },
    {
      id: 'sports',
      name: 'Sports & Outdoors',
      icon: '🏋️',
      subcategories: [
        'Gym Equipment',
        'Fitness Gear',
        'Sportswear',
        'Football Equipment',
        'Basketball Equipment',
        'Running Gear',
        'Cycling Equipment',
        'Camping Gear',
        'Hiking Equipment',
        'Outdoor Furniture',
        'Fishing Gear',
        'Water Sports',
        'Martial Arts Equipment',
        'Yoga Equipment',
        'Training Accessories',
        'Sports Accessories',
        'Sports Shoes',
        'Outdoor Tools',
        'Survival Gear',
        'Fitness Accessories',
      ],
    },
  ],
  secondhand: [
    {
      id: 'electronics',
      name: 'Electronics',
      icon: '💻',
      subcategories: [
        'Phones & Tablets',
        'Laptops & Computers',
        'TVs & Audio',
        'Cameras',
        'Video Games',
      ],
      itemTypes: {
        'Phones & Tablets': ['Used Smartphones', 'Used Tablets', 'Used Smartwatches'],
        'Laptops & Computers': ['Refurbished Laptops', 'Used Desktops', 'Used Monitors'],
        'TVs & Audio': ['Used Smart TVs', 'Pre-owned Home Theaters', 'Speakers'],
        'Video Games': ['Used Consoles', 'Used Games', 'Used Gear'],
      },
    },
    {
      id: 'furniture',
      name: 'Furniture',
      icon: '🛋️',
      subcategories: ['Sofas & Living Room', 'Beds & Bedroom', 'Tables & Desks', 'Chairs'],
      itemTypes: {
        'Sofas & Living Room': ['Used Sofas', 'Used Recliners', 'Used TV Stands'],
        'Beds & Bedroom': ['Used Bed Frames', 'Pre-owned Wardrobes', 'Nightstands'],
      },
    },
    {
      id: 'clothing',
      name: 'Clothing & Fashion',
      icon: '👕',
      subcategories: ["Men's Clothing", "Women's Clothing", 'Kids Clothing', 'Shoes', 'Bags'],
      itemTypes: {
        "Men's Clothing": [
          'T-Shirts',
          'Shirts',
          'Hoodies',
          'Jackets & Coats',
          'Jeans & Trousers',
          'Suits & Blazers',
          'Sportswear',
          'Traditional Wear',
        ],
        "Women's Clothing": [
          'Dresses & Gowns',
          'Tops & Blouses',
          'Skirts',
          'Jackets & Coats',
          'Jeans & Trousers',
          'Lingerie',
          'Traditional Wear',
        ],
        Shoes: ['Sneakers', 'Formal Shoes', 'Boots', 'Sandals & Slippers'],
      },
    },
    {
      id: 'vehicles',
      name: 'Vehicles',
      icon: '🚗',
      subcategories: ['Cars', 'Motorcycles', 'Vehicle Parts'],
    },
    { id: 'books', name: 'Books & Hobbies', icon: '📚' },
    { id: 'tools', name: 'Tools & DIY', icon: '🛠️' },
    {
      id: 'appliances',
      name: 'Home Appliances',
      icon: '📺',
      subcategories: ['Fridges & Freezers', 'Cookers & Ovens', 'Washing Machines', 'Microwaves'],
    },
    {
      id: 'sports-leisure',
      name: 'Sports & Leisure',
      icon: '⚽',
      subcategories: ['Gym Equipment', 'Camping Gear', 'Bicycles', 'Outdoor Games'],
    },
    {
      id: 'musical-instruments',
      name: 'Musical Instruments',
      icon: '🎸',
      subcategories: ['Guitars', 'Keyboards', 'Drums', 'Audio Gear'],
    },
    {
      id: 'collectibles',
      name: 'Collectibles & Art',
      icon: '🎨',
      subcategories: ['Antiques', 'Art & Paintings', 'Stamps & Coins', 'Vintage Decor'],
    },
    {
      id: 'baby-items',
      name: 'Baby & Kids',
      icon: '👶',
      subcategories: ['Baby Clothes', 'Strollers', 'Toys', 'Nursery Furniture'],
    },
  ],
  wholesalers: [
    {
      id: 'fmcg',
      name: 'FMCG & Groceries',
      icon: '🛒',
      subcategories: [
        'Dry Foods',
        'Beverages',
        'Personal Care',
        'Household Supplies',
        'Stationery',
      ],
      itemTypes: {
        'Dry Foods': ['Maize Flour (Bales)', 'Sugar', 'Rice', 'Cooking Oil', 'Salt'],
        Beverages: ['Soft Drinks', 'Water', 'Energy Drinks', 'Juices'],
        'Household Supplies': ['Soaps & Detergents', 'Tissue Paper', 'Cleaning Agents'],
      },
    },
    {
      id: 'hardware',
      name: 'Hardware & Construction',
      icon: '🏗️',
      subcategories: [
        'Cement & Aggregates',
        'Steel & Metal',
        'Electrical Supplies',
        'Plumbing Materials',
        'Paint & Finishes',
      ],
      itemTypes: {
        'Cement & Aggregates': ['Cement', 'Sand', 'Ballast', 'Bricks'],
        'Steel & Metal': ['Rebars (Y-Steel)', 'Mabati (Roofing Sheets)', 'Wire Mesh'],
        'Electrical Supplies': ['Conduits', 'Cables', 'Switchgear', 'Lighting Fixtures'],
      },
    },
    {
      id: 'textiles',
      name: 'Textiles & Fabrics',
      icon: '🧵',
      subcategories: ['Uniform Fabrics', 'Upholstery', 'Curtain Fabrics', 'Tailoring Accessories'],
    },
    {
      id: 'mitumba',
      name: 'Mitumba Bales',
      icon: '🧺',
      subcategories: [
        'Grade A Bales',
        'Grade B Bales',
        'Mixed Bales',
        'Shoes Bales',
        'Toys Bales',
        'Children Wear Bales',
        'Household Bales',
        'Premium Boutique Bales',
      ],
      itemTypes: {
        'Grade A Bales': ['Camera Grade', 'Grade A Mix', 'First Selection'],
        'Grade B Bales': ['Standard Bales', 'Economy Bales'],
        'Shoes Bales': ['Sneakers Bales', 'Official Shoes Bales', 'Kids Shoes Bales'],
      },
    },
    {
      id: 'kitchenware',
      name: 'Bulk Kitchenware',
      icon: '🍳',
      subcategories: [
        'Cookware Sets',
        'Tableware',
        'Small Appliances',
        'Plasticware',
        'Cutlery',
        'Kamukunji Mix Bales',
      ],
    },
    {
      id: 'packaging',
      name: 'Packaging Materials',
      icon: '📦',
      subcategories: ['Boxes & Cartons', 'Plastic Packages', 'Labels & Tags', 'Shipping Supplies'],
    },
    {
      id: 'chemicals',
      name: 'Industrial Chemicals',
      icon: '🧪',
      subcategories: ['Agro-Chemicals', 'Cleaning Chemicals', 'Paints Raw Materials'],
    },
  ],
  digital: [
    { id: 'software', name: 'Software & Apps', icon: '💾' },
    { id: 'gaming', name: 'Gaming & Keys', icon: '🎮' },
    { id: 'accounts', name: 'Premium Accounts', icon: '👤' },
    { id: 'assets', name: 'Digital Assets', icon: '🎨' },
    { id: 'courses', name: 'Online Courses', icon: '🎓' },
  ],
  farmer: [
    {
      id: 'produce',
      name: 'Fresh Produce',
      icon: '🍎',
      subcategories: ['Vegetables', 'Fruits', 'Grains & Cereals', 'Legumes', 'Tubers & Roots'],
      itemTypes: {
        Vegetables: ['Tomatoes', 'Onions', 'Cabbages', 'Sukuma Wiki', 'Kales', 'Managu', 'Carrots'],
        Fruits: ['Mangoes', 'Avocados', 'Oranges', 'Bananas', 'Watermelons', 'Passion Fruit'],
        'Grains & Cereals': ['Dry Maize', 'Wheat', 'Millet', 'Sorghum', 'Rice'],
        Legumes: ['Beans', 'Peas', 'Green Grams (Ndengu)', 'Lentils'],
      },
    },
    {
      id: 'livestock',
      name: 'Livestock',
      icon: '🐄',
      subcategories: ['Cattle', 'Sheep & Goats', 'Pigs', 'Rabbits', 'Donkeys'],
      itemTypes: {
        Cattle: ['Dairy Cows', 'Beef Bulls', 'Calves'],
        'Sheep & Goats': ['Dairy Goats', 'Boer Goats', 'Meat Sheep'],
        Pigs: ['Large White', 'Duroc', 'Landrace'],
      },
    },
    {
      id: 'poultry',
      name: 'Poultry',
      icon: '🐔',
      subcategories: ['Chickens', 'Eggs', 'Turkeys', 'Ducks & Geese'],
      itemTypes: {
        Chickens: ['Kienyeji (Improved)', 'Layers', 'Broilers'],
        Eggs: ['Kienyeji Eggs', 'Grade Eggs', 'Hatched Eggs'],
      },
    },
    {
      id: 'seeds',
      name: 'Seeds & Fertilizers',
      icon: '🌱',
      subcategories: [
        'Hybrid Seeds',
        'Local Seeds',
        'Fertilizers',
        'Animal Feed',
        'Agro-Chemicals',
      ],
      itemTypes: {
        Fertilizers: ['DAP', 'CAN', 'Urea', 'NPK', 'Foliar'],
        'Animal Feed': ['Dairy Meal', 'Layer Mash', 'Broiler Starter', 'Hay Bales'],
      },
    },
    {
      id: 'machinery',
      name: 'Farm Machinery',
      icon: '🚜',
      subcategories: [
        'Tractors',
        'Irrigation Systems',
        'Processing Tools',
        'Storage Silos',
        'Small Farm Tools',
      ],
    },
    {
      id: 'processing',
      name: 'Agro-Processing',
      icon: '🏭',
      subcategories: [
        'Milling Services',
        'Packing & Branding',
        'Oil Extraction',
        'Cold Storage Services',
        'Drying & Silos',
      ],
    },
  ],
  hotels: [
    { id: 'hotels-resorts', name: 'Hotels & Resorts', icon: '🏨' },
    { id: 'airbnb-homestays', name: 'Airbnb & Homestays', icon: '🏠' },
    { id: 'guest-houses', name: 'Guest Houses & B&Bs', icon: '🏡' },
    { id: 'safari-lodges', name: 'Safari Lodges & Camps', icon: '⛺' },
    { id: 'serviced-apartments', name: 'Serviced Apartments', icon: '🏙️' },
    { id: 'beach-resorts', name: 'Beach Resorts', icon: '🏖️' },
  ],
  services: [
    {
      id: 'home-services',
      name: 'Home Services',
      icon: '🏠',
      subcategories: [
        'Mama Fua',
        'General Cleaning',
        'Deep Cleaning',
        'Sofa Cleaning',
        'Carpet Cleaning',
        'Carpet Installation',
        'Mattress Cleaning',
        'Curtain Cleaning',
        'Laundry Services',
        'Ironing Services',
        'Fumigation',
        'Pest Control',
        'Garbage Collection',
        'Water Tank Cleaning',
        'Septic Tank Cleaning',
        'Dishwashing Services',
        'Home Sanitization',
        'Window Cleaning',
        'Floor Polishing',
        'Post Construction Cleaning',
        'Office Cleaning',
        'Airbnb Cleaning',
        'Move-in Cleaning',
        'Move-out Cleaning',
        'Housekeeping',
        'Compound Cleaning',
      ],
    },
    {
      id: 'repairs',
      name: 'Repairs & Maintenance',
      icon: '🔧',
      subcategories: [
        'Plumbing',
        'Electrical Services',
        'Appliance Repair',
        'Fridge Repair',
        'Cooker Repair',
        'Washing Machine Repair',
        'TV Repair',
        'Generator Repair',
        'Water Pump Repair',
        'Borehole Repair',
        'CCTV Installation',
        'Electric Fence Installation',
        'Gate Automation',
        'Intercom Installation',
        'WiFi Installation',
        'Solar Installation',
        'Solar Repair',
        'Door Repair',
        'Window Repair',
        'Lock Installation',
        'Locksmith Services',
        'Furniture Repair',
        'Roofing Repair',
        'Ceiling Repair',
        'Painting Repair',
      ],
    },
    {
      id: 'moving',
      name: 'Moving & Logistics',
      icon: '🚚',
      subcategories: [
        'House Moving',
        'Office Moving',
        'Pickup Hire',
        'Lorry Transport',
        'Furniture Moving',
        'Parcel Delivery',
        'Courier Services',
        'Motorbike Delivery',
        'Taxi Services',
        'Car Hire',
        'Van Hire',
        'Driver Services',
        'Loading Services',
        'Offloading Services',
        'Packing Services',
        'Unpacking Services',
        'Storage Services',
        'Relocation Services',
        'Moving Labor',
        'Equipment Transport',
      ],
    },
    {
      id: 'events',
      name: 'Events & Catering',
      icon: '🎉',
      subcategories: [
        'Event Planning',
        'Catering Services',
        'Photography',
        'Videography',
        'DJ Services',
        'Tent Hire',
        'Chairs Hire',
        'Makeup Artist',
        'Hairdressing',
        'Sound System Hire',
        'MC Services',
        'Decor Services',
        'Event Lighting',
        'Wedding Planning',
        'Birthday Planning',
        'Corporate Events',
        'Graduation Events',
        'Baby Shower Planning',
        'Party Equipment Hire',
        'Event Security',
      ],
    },
    {
      id: 'beauty-wellness',
      name: 'Beauty & Wellness',
      icon: '💅',
      subcategories: [
        'Salon & Hairdressing',
        'Barber Services',
        'Makeup Artists',
        'Massage & Spa',
        'Fitness Training',
        'Yoga Instructors',
        'Nutritionists',
        'Personal Trainers',
      ],
    },
    {
      id: 'professional',
      name: 'Professional Services',
      icon: '💼',
      subcategories: [
        'Legal Services',
        'Accounting & Audit',
        'IT & Web Design',
        'Marketing & Branding',
        'Business Consulting',
        'Graphic Design',
        'Writing & Translation',
        'Architecture',
      ],
    },
    {
      id: 'tutoring',
      name: 'Tutoring & Lessons',
      icon: '📚',
      subcategories: [
        'Primary & High School',
        'Music Lessons',
        'Language Classes',
        'Driving School',
        'Coding Classes',
        'Arts & Crafts',
        'Cooking Lessons',
      ],
    },
  ],
  live: [
    { id: 'live-shopping', name: 'Live Shopping', icon: '🔴' },
    { id: 'auctions', name: 'Live Auctions', icon: '🔨' },
  ],
  logistics: [
    {
      id: 'moving-services',
      name: 'Moving & Relocation',
      icon: '🚛',
      subcategories: [
        'House Moving',
        'Office Relocation',
        'Single Item Moving',
        'Piano & Heavy Items',
        'Long Distance Moving',
        'International Moving',
      ],
    },
    {
      id: 'courier-delivery',
      name: 'Courier & Delivery',
      icon: '📦',
      subcategories: [
        'Same Day Delivery',
        'Next Day Delivery',
        'Parcel Pickup',
        'Food Delivery',
        'Document Courier',
        'E-commerce Fulfillment',
      ],
    },
    {
      id: 'freight-transport',
      name: 'Freight & Transport',
      icon: '🚢',
      subcategories: [
        'Road Freight',
        'Air Cargo',
        'Sea Freight',
        'Refrigerated Cargo',
        'Bulk Transport',
        'Cross-border Logistics',
      ],
    },
    {
      id: 'warehousing',
      name: 'Warehousing & Storage',
      icon: '🏭',
      subcategories: [
        'Short Term Storage',
        'Long Term Warehousing',
        'Cold Storage',
        'Customs Bonded Warehouse',
        'Last Mile Distribution',
      ],
    },
  ],
  tech: [
    {
      id: 'phones-tablets',
      name: 'Phones & Tablets',
      icon: '📱',
      subcategories: ['Mobile Phones & Tablets'],
      itemTypes: {
        'Mobile Phones & Tablets': [
          'Smartphones',
          'Feature Phones',
          'Tablets (Android)',
          'Tablets (iPad)',
          'Smartwatches',
          'Kindles & E-readers',
        ],
      },
    },
    {
      id: 'computers-laptops',
      name: 'Laptops & Computers',
      icon: '💻',
      subcategories: ['Computers & IT'],
      itemTypes: {
        'Computers & IT': [
          'Laptops',
          'Desktop Computers',
          'Monitors',
          'Printers & Scanners',
          'Networking Equipment (Routers)',
          'External Storage',
          'Computer Accessories',
        ],
      },
    },
    {
      id: 'tv-audio',
      name: 'TVs & Audio Systems',
      icon: '📺',
      subcategories: ['TV & Home Audio'],
      itemTypes: {
        'TV & Home Audio': [
          'Smart TVs',
          'OLED/QLED TVs',
          'Home Theater Systems',
          'Soundbars',
          'Bluetooth Speakers',
          'Headphones',
          'Earphones & Buds',
        ],
      },
    },
    {
      id: 'gaming',
      name: 'Gaming Gear',
      icon: '🎮',
      subcategories: ['Gaming'],
    },
    {
      id: 'photography',
      name: 'Photography & Drones',
      icon: '📷',
      subcategories: ['Photography & Drones'],
    },
  ],
};
